import { ARMY, NIGHTMARES, type NightmareType } from '@somnhold/content';
import { runBattle } from '../battle/step.ts';
import { createBattle } from '../battle/create.ts';
import { idiv, imin, mulBp } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng, type RngState } from '../math/rng.ts';
import type { BattleState } from '../types.ts';
import { draftBudget, draftableKinds, fitArmy, planFor, type ArmyPick } from '../wave.ts';
import { waveBattleParams, waveSeed, waveSpecOf } from './resolve.ts';
import { levelMarea } from './level.ts';
import type { CampaignState, Ms } from './types.ts';

/*
 * The enemy general drafts its army (docs/TACTICAS.md): between the wave before and this one it tries armies
 * against the bastion as it stands (everyone's ghosts defending, on a scouting seed of its own), a few at a time,
 * and keeps the one that hurt most. Half its tries are new armies, half change its best so far. The wave comes as
 * that army; with no try done, it is drawn as ever.
 */

/** How many armies the general tries for a wave (0: it doesn't draft it). */
export const draftTries = (s: CampaignState, now: Ms, marea: number, wave: number): number => {
  const step = levelMarea(s);
  if (step < ARMY.fromMarea || marea < (s.draftFrom ?? 0)) return 0;
  const spec = waveSpecOf(s, now, marea, wave);
  if (!planFor(spec) || draftableKinds(spec).length < ARMY.minKinds) return 0;
  return ARMY.candidates[imin(step, ARMY.candidates.length - 1)] as number;
};

/** True while the general still has armies to try for the current wave. */
export const drafting = (s: CampaignState, now: Ms): boolean => {
  if (s.status !== 'active' || s.mareaLost) return false;
  const d = s.draft;
  const tried = d && d.marea === s.mareaIndex && d.wave === s.waveIndex ? d.tried : 0;
  return tried < draftTries(s, now, s.mareaIndex, s.waveIndex);
};

/** How much a battle hurt the bastion: a fallen keep above all, then the keep's damage, then what fell. */
export const draftHurt = (b: BattleState): number =>
  (b.result === 'lost' ? 1_000_000 : 0) + idiv((b.keep.maxHp - (b.keep.hp > 0 ? b.keep.hp : 0)) * 10000, b.keep.maxHp > 0 ? b.keep.maxHp : 1) + 100 * b.structures.filter((x) => x.destroyed).length;

/** Points per kind of an army. */
const pointsOf = (army: ArmyPick): Map<NightmareType, number> => {
  const m = new Map<NightmareType, number>();
  for (const x of army) m.set(x.kind, (m.get(x.kind) ?? 0) + x.n * NIGHTMARES[x.kind].points);
  return m;
};

/**
 * An army bought one nightmare at a time: each pick a kind by its weight (none past its cap) and a sector by
 * the lean (sector 1's share in bp, with two; else evenly), until the budget runs out.
 */
const buy = (rng: RngState, sectors: number, budget: number, weights: Map<NightmareType, number>, leanBp: number): ArmyPick => {
  const cap = mulBp(budget, ARMY.maxKindBp);
  const spent = new Map<NightmareType, number>();
  const n = new Map<string, number>();
  let left = budget;
  for (let guard = 0; guard < 4000; guard++) {
    const open = [...weights].filter(([k, w]) => w > 0 && NIGHTMARES[k].points <= left && (spent.get(k) ?? 0) + NIGHTMARES[k].points <= cap);
    if (!open.length) break;
    const total = open.reduce((a, [, w]) => a + w, 0);
    let r = rngInt(rng, total);
    let kind = open[0]![0];
    for (const [k, w] of open) {
      if (r < w) {
        kind = k;
        break;
      }
      r -= w;
    }
    let sector = 0;
    if (sectors === 2) sector = rngInt(rng, 10000) < leanBp ? 1 : 0;
    else if (sectors > 2) sector = rngInt(rng, sectors);
    const key = `${sector}:${kind}`;
    n.set(key, (n.get(key) ?? 0) + 1);
    spent.set(kind, (spent.get(kind) ?? 0) + NIGHTMARES[kind].points);
    left -= NIGHTMARES[kind].points;
  }
  return [...n].map(([key, c]) => ({ sector: Number(key.split(':')[0]), kind: key.split(':')[1] as NightmareType, n: c }));
};

/** The `i`-th army the general tries for the current wave: a new one, or its best changed a little. */
export const draftCandidate = (s: CampaignState, now: Ms, i: number): ArmyPick | null => {
  const marea = s.mareaIndex;
  const wave = s.waveIndex;
  const spec = waveSpecOf(s, now, marea, wave);
  const kinds = draftableKinds(spec);
  const budget = draftBudget(spec);
  const rng = seedRng(deriveSeed(waveSeed(s), 70000 + marea * 256 + wave * 16 + i));
  const best = s.draft && s.draft.marea === marea && s.draft.wave === wave ? s.draft.army : null;
  const weights = new Map<NightmareType, number>();
  let leanBp: number;
  if (best && i % 2 === 1) {
    // From its best: one kind gives up a share of its points to another (maybe a new one), the lean nudged.
    const pts = pointsOf(best);
    for (const k of kinds) weights.set(k, pts.get(k) ?? 0);
    const had = kinds.filter((k) => (weights.get(k) ?? 0) > 0);
    const from = had[rngInt(rng, had.length)]!;
    const to = kinds[rngInt(rng, kinds.length)]!;
    const moved = mulBp(weights.get(from) ?? 0, ARMY.mutateBp);
    weights.set(from, (weights.get(from) ?? 0) - moved);
    weights.set(to, (weights.get(to) ?? 0) + moved);
    const total = best.reduce((a, x) => a + x.n * NIGHTMARES[x.kind].points, 0);
    const one = best.filter((x) => x.sector === 1).reduce((a, x) => a + x.n * NIGHTMARES[x.kind].points, 0);
    leanBp = total > 0 ? idiv(one * 10000, total) + rngInt(rng, 2001) - 1000 : 5000;
  } else {
    // A new one: weights squared, so a few kinds lead.
    for (const k of kinds) {
      const w = rngInt(rng, 101);
      weights.set(k, w * w);
    }
    leanBp = rngInt(rng, 10001);
  }
  return fitArmy(spec, buy(rng, spec.sectors, budget, weights, leanBp));
};

/**
 * Tries up to `n` more armies for the current wave (once the wave before it is over: it is the current one),
 * keeping the best in `s.draft`. True when it tried any.
 */
export const draftStep = (s: CampaignState, now: Ms, n: number): boolean => {
  let any = false;
  for (let k = 0; k < n && drafting(s, now); k++) {
    const marea = s.mareaIndex;
    const wave = s.waveIndex;
    const cur = s.draft && s.draft.marea === marea && s.draft.wave === wave ? s.draft : null;
    const tried = cur?.tried ?? 0;
    const army = draftCandidate(s, now, tried);
    any = true;
    if (!army) {
      if (cur) cur.tried++;
      else s.draft = { marea, wave, army: [], tried: 1, hurt: -1 };
      continue;
    }
    // The scouting battle: this army, everyone's ghosts, a seed of its own (not the wave's).
    const params = waveBattleParams({ ...s, draft: { marea, wave, army, tried, hurt: 0 } }, now, { marea, wave });
    const b = createBattle({ ...params, seed: deriveSeed(waveSeed(s), 71000 + marea * 16 + wave) });
    runBattle(b);
    const hurt = draftHurt(b);
    if (!cur || cur.hurt < hurt || !cur.army.length) s.draft = { marea, wave, army, tried: tried + 1, hurt };
    else cur.tried++;
  }
  return any;
};

/** What the omen lets out about a drafted army (never what it is, only what most of it looks like). */
export type DraftHint = 'air' | 'siege' | 'range' | 'swarm' | 'heavy' | 'foot' | 'mixed';

/**
 * The omen's hint at a wave's drafted army: null when the general doesn't draft it, 'pending' while it has
 * tried nothing yet.
 */
export const draftHint = (s: CampaignState, now: Ms, marea: number, wave: number): DraftHint | 'pending' | null => {
  if (draftTries(s, now, marea, wave) === 0) return null;
  const d = s.draft && s.draft.marea === marea && s.draft.wave === wave ? s.draft : null;
  const army = d ? fitArmy(waveSpecOf(s, now, marea, wave), d.army) : null;
  if (!army) return d && d.tried > 0 ? null : 'pending';
  const by = new Map<DraftHint, number>();
  let total = 0;
  for (const x of army) {
    const n = NIGHTMARES[x.kind];
    const pts = x.n * n.points;
    const kind: DraftHint = n.flying
      ? 'air'
      : n.siege || n.lobs || n.wallBp || n.burrows
        ? 'siege'
        : n.shootsTowers || n.shootsHeroes
          ? 'range'
          : n.points <= ARMY.hintSwarmPoints
            ? 'swarm'
            : n.points >= ARMY.hintHeavyPoints
              ? 'heavy'
              : 'foot';
    by.set(kind, (by.get(kind) ?? 0) + pts);
    total += pts;
  }
  let best: DraftHint = 'mixed';
  let most = mulBp(total, ARMY.hintShareBp) - 1;
  for (const k of ['air', 'siege', 'range', 'swarm', 'heavy', 'foot'] as DraftHint[]) {
    const v = by.get(k) ?? 0;
    if (v > most) {
      most = v;
      best = k;
    }
  }
  return best;
};
